import { DeviceVariantsEnum } from '@ubnt/link-core/enums';
import { useBottomPanel } from '@ubnt/link-core-ui/components/BottomPanel/context/BottomPanelContext';
import { useCallback } from 'react';

import { useDispatch, useSelector } from 'app/store';
import {
  devicesDeviceChannelWidthsSelector,
  devicesDeviceVariantFrequencyLabelSelector,
  devicesDeviceVariantParamsUpdate,
  devicesDeviceVariantSelector,
  devicesMeshDeviceVariantSelector,
} from 'modules/devices/slice';
import {
  linksDeviceChannelWidthSelector,
  linksMeshUplinkApIdSelector,
  linksMeshUplinkIdSelector,
} from 'modules/links/slice';
import { trackEvent } from 'utils/googleAnalytics';

import { linksChannelWidthWarningSelector } from 'modules/links/externalSelectors';
import TabsPicker, { TabsPickerProps } from 'modules/bottomPanel/components/elements/TabsPicker';

import classes from 'modules/bottomPanel/components/elements/TabsPicker.module.scss';

const ChannelWidthPicker = (props: Partial<TabsPickerProps>) => {
  const { leftId, linkId } = useBottomPanel();

  const variant = useSelector((state) => (leftId ? devicesDeviceVariantSelector(state, leftId) : undefined));
  const uplinkApId = useSelector((state) => (linkId ? linksMeshUplinkApIdSelector(state, linkId) : false));
  const uplinkId = useSelector((state) => (linkId ? linksMeshUplinkIdSelector(state, linkId) : undefined));
  const uplinkVariant = useSelector((state) => (uplinkId ? devicesMeshDeviceVariantSelector(state) : undefined));
  const controlsUplinkAp = uplinkVariant === variant && uplinkApId;
  const changeId = controlsUplinkAp ? uplinkApId : leftId;
  const changeLinkId = controlsUplinkAp ? uplinkId : linkId;
  const options = useSelector(
    (state) =>
      changeId && devicesDeviceChannelWidthsSelector(state, changeId, devicesDeviceVariantSelector(state, changeId))
  );
  const value = useSelector(
    (state) =>
      changeLinkId &&
      changeId &&
      linksDeviceChannelWidthSelector(state, changeLinkId, devicesDeviceVariantSelector(state, changeId))
  );
  const { labelTooltip, labelTooltipProps } = useSelector((state) =>
    linkId ? linksChannelWidthWarningSelector(state, linkId) : { labelTooltip: undefined, labelTooltipProps: undefined }
  );
  const frequencyLabel = useSelector((state) =>
    leftId ? devicesDeviceVariantFrequencyLabelSelector(state, leftId) : undefined
  );
  const isLongLabelVariant =
    variant === DeviceVariantsEnum.High5GHz ||
    variant === DeviceVariantsEnum.High6GHz ||
    variant === DeviceVariantsEnum.Low5GHz ||
    variant === DeviceVariantsEnum.Low6GHz ||
    variant === DeviceVariantsEnum.Mid5GHzSub3 ||
    variant === DeviceVariantsEnum.High5GHzSub3;

  const label = `${frequencyLabel ? `${frequencyLabel} ` : ''}Channel Width ${isLongLabelVariant && labelTooltip ? '' : '(MHz)'}`;

  const dispatch = useDispatch();

  const onChange = useCallback(
    (newValue: string) => {
      if (changeId && variant != null) {
        const channelWidth = Number(newValue);
        dispatch(devicesDeviceVariantParamsUpdate({ id: changeId, variant, changes: { channelWidth } }));
        trackEvent('bottomPanelChannelWidthChange', { variant, channelWidth });
      }
    },
    [dispatch, changeId, variant]
  );

  if (!leftId || !options || !value) {
    return null;
  }

  return (
    <TabsPicker
      isDisabled={!!controlsUplinkAp}
      label={label}
      labelTooltip={labelTooltip}
      labelTooltipProps={labelTooltipProps}
      labelClassName={isLongLabelVariant ? classes.shrinkLabel : undefined}
      onChange={onChange}
      options={options}
      testId="channel-width-picker"
      value={value.toString()}
      tooltipDescription={controlsUplinkAp ? 'Channel width is controlled by the uplink AP' : undefined}
      {...props}
    />
  );
};

export default ChannelWidthPicker;
